/**
 * Grades tab — student detail page (Prompt 11E.8, D5).
 *
 * "Grades" is Phase 10B's Student Progress Report (`instructor` type),
 * surfaced here — no new report type, no schema change. Lists this
 * student's SUBMITTED instructor reports (drafts stay private to their
 * author) with period, instructor, Download PDF, and Re-fill (30-day
 * archive rule, backend-enforced).
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import {
  useStaffReports,
  useRefillStaffReport,
  usePdfUrl,
  type StaffReport,
} from '@features/reports/staff/api';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Period = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const Instructor = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const ActionButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 6px 10px;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.background}; }
`;

function GradeRow({ report }: { report: StaffReport }) {
  const [pdfEnabled, setPdfEnabled] = useState(false);
  const pdfQuery = usePdfUrl('instructor', report.id, pdfEnabled);
  const refillMutation = useRefillStaffReport('instructor');

  React.useEffect(() => {
    if (pdfEnabled && pdfQuery.data?.download_url) {
      window.open(pdfQuery.data.download_url, '_blank', 'noopener,noreferrer');
      setPdfEnabled(false);
    }
  }, [pdfEnabled, pdfQuery.data]);

  const handleRefill = async () => {
    try {
      await refillMutation.mutateAsync(report.id);
      toast.success('New draft created from this report — continue it from My Drafts.');
    } catch {
      toast.error('Could not create a refill draft.');
    }
  };

  return (
    <Row data-testid={`candeza-student-grades-row-${report.id}`}>
      <Meta>
        <Period>
          {report.period_start} – {report.period_end}
        </Period>
        <Instructor>{report.instructor_name || report.author_name}</Instructor>
      </Meta>
      <Actions>
        <ActionButton
          data-testid={`candeza-student-grades-pdf-${report.id}`}
          onClick={() => setPdfEnabled(true)}
          disabled={pdfEnabled && pdfQuery.isLoading}
        >
          {pdfEnabled && pdfQuery.isLoading ? 'Opening…' : 'Download PDF'}
        </ActionButton>
        <ActionButton
          data-testid={`candeza-student-grades-refill-${report.id}`}
          onClick={handleRefill}
          disabled={refillMutation.isPending}
        >
          Re-fill
        </ActionButton>
      </Actions>
    </Row>
  );
}

export const StudentGradesTab: React.FC<{ studentId: string }> = ({ studentId }) => {
  const { data, isLoading, error } = useStaffReports('instructor', {
    student_id: studentId,
    status: 'submitted',
  });

  if (isLoading) {
    return <div data-testid="candeza-student-grades-loading">Loading grades…</div>;
  }
  if (error) {
    return (
      <div data-testid="candeza-student-grades-error" style={{ color: '#dc2626' }}>
        Could not load grades.
      </div>
    );
  }
  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <div data-testid="candeza-student-grades-empty" style={{ color: '#64748b' }}>
        No submitted progress reports yet.
      </div>
    );
  }

  return (
    <Wrapper data-testid="candeza-student-grades-tab">
      {items.map((report) => (
        <GradeRow key={report.id} report={report} />
      ))}
    </Wrapper>
  );
};
